import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Paginator } from 'primeng/paginator';
import { HighlighterPipe } from 'src/app/core/pipes/highlighter.pipe';
import { GridCountDialogComponent } from './components/grid-count-dialog/grid-count-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { ProfileService } from 'src/app/core/services/profile.service';

@Component({
  selector: 'acv-grid',
  templateUrl: './acv-grid.component.html',
  styleUrls: ['./acv-grid.component.scss'],
})
export class ACVGridComponent implements OnInit,OnDestroy {
  grid_columns: any;
  grid_data: any;
  active_row_data: any;
  allow_selection: boolean = false;
  allow_Headerselection: boolean = true;
  allow_sorting: boolean = false;
  reorderableColumns: boolean = false;
  allow_columnReOrder: boolean = false;
  allow_Pagination: boolean = true;
  show_livelevelActions: boolean = true;
  show_EditIcon: boolean = false;
  show_DeleteIcon: boolean = false;
  show_PodIcon: boolean = false;
  show_AttachmentIcon: boolean = false;
  show_RowGroup: boolean = false;
  freeze_actionColumn: boolean = false;
  selectedRecords: any;
  line_levelActions: MenuItem[] = [];
  hold_gridSorting: boolean = false;
  recordCountPerPage: any = [];
  totalRecords: any = [];
  totalRecordsPerPage: any = 50;
  highlightedSearchItems: any = '';
  allowHighlightSearching: boolean = false;
  currentHoveredCellId: string = '';
  lastPage: any = '';
  firstPage: any = '';
  currentPageReport: any;
  pagesize_selected: number = 50;
  sortField: string | null = null;
  sortOrder: number | null = null;
  evenrows: number | null = null;
  module_name:string = null;

  rowExpansion: {
    enabled?: boolean;
    /**
     * Field on the main row object to use as unique key for expansion state.
     * Example: 'trip_id'
     */
    keyField?: string;
    /**
     * Field on the main row object containing the nested array.
     * Example: 'trip_details'
     */
    detailsField: string;
    /**
     * Column definitions for nested table.
     */
    columns: any[];
    emptyMessage?: string;
    /**
     * Optional callback to add a class to each nested (expanded) row.
     * Return a string (class name) or an object used by ngClass.
     */
    rowClass?: (nestedRow: any, parentRow: any) => string | string[] | Record<string, boolean> | null | undefined;
  } | null = null;
  expandedRowKeys: Record<string, boolean> = {};
  private persistExpandedRowsKey: string | null = null;

  /**
   * Enable persistence of expanded rows by providing a stable key.
   * Example: "lc-dashboard-list"
   *
   * This is intentionally opt-in so other grids keep current behavior.
   */
  @Input('PersistExpandedRowsKey') set _PersistExpandedRowsKey(value: string | null) {
    this.persistExpandedRowsKey = value ? String(value) : null;
    // Attempt restore whenever key changes (or becomes enabled)
    this.restoreExpandedRowKeys();
  }

  @Output() SelectionChange = new EventEmitter();
  @Output() AllSelect = new EventEmitter();
  @Output() PodClick = new EventEmitter();

  @Input('GridColumns') set _GridColumns(value: any[]) {
    this.grid_columns = value ? value.filter((x: any) => x.visible) : [];
  }

  @Input('GridDataSource') set _GridDataSource(value: any) {
    this.grid_data = value ? value : [];
     this.resetProcessedRows();
     // When new data arrives, drop expanded keys that no longer exist
     this.reconcileExpandedRowKeysWithData();
  }

  @Input('RowExpansion') set _RowExpansion(value: any) {
    // Backwards compatible: row expansion is disabled unless explicitly enabled
    if (value && value.enabled && value.detailsField && value.columns) {
      this.rowExpansion = value;
    } else {
      this.rowExpansion = null;
    }
    // Reset expansion state any time config changes
    this.expandedRowKeys = {};
    // If persistence is enabled, try to restore right after config changes
    this.restoreExpandedRowKeys();
  }

  private getExpandedRowsStorageKey(): string | null {
    if (!this.persistExpandedRowsKey) return null;
    return `acv.grid.expandedRowKeys.${this.persistExpandedRowsKey}`;
  }

  private restoreExpandedRowKeys(): void {
    const storageKey = this.getExpandedRowsStorageKey();
    if (!storageKey) return;
    if (!this.rowExpansion) return; // only meaningful when expansion is enabled

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      const next: Record<string, boolean> = {};
      parsed.forEach((k) => {
        const key = String(k);
        if (key) next[key] = true;
      });
      this.expandedRowKeys = next;
      this.reconcileExpandedRowKeysWithData();
    } catch {
      // ignore invalid JSON / blocked storage
    }
  }

  private persistExpandedRowKeys(): void {
    const storageKey = this.getExpandedRowsStorageKey();
    if (!storageKey) return;
    if (!this.rowExpansion) return;

    try {
      const keys = Object.keys(this.expandedRowKeys || {}).filter((k) => this.expandedRowKeys[k]);
      if (keys.length === 0) {
        // Remove from localStorage when no rows are expanded
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(keys));
      }
    } catch {
      // ignore quota / blocked storage
    }
  }

  private reconcileExpandedRowKeysWithData(): void {
    if (!this.rowExpansion) return;
    if (!this.expandedRowKeys || !Object.keys(this.expandedRowKeys).length) return;
    if (!Array.isArray(this.grid_data) || !this.grid_data.length) return;

    const keyField = this.getRowExpansionKeyField();
    const allowed = new Set<string>(
      this.grid_data
        .map((row: any) => row?.[keyField])
        .filter((v: any) => v !== undefined && v !== null)
        .map((v: any) => String(v))
    );

    let changed = false;
    Object.keys(this.expandedRowKeys).forEach((k) => {
      if (!allowed.has(k)) {
        delete this.expandedRowKeys[k];
        changed = true;
      }
    });

    if (changed) this.persistExpandedRowKeys();
  }

  onRowExpand(evnt: any): void {
    if (!this.rowExpansion) return;
    const keyField = this.getRowExpansionKeyField();
    const key = evnt?.data?.[keyField];
    if (key === undefined || key === null) return;

    this.expandedRowKeys = { ...(this.expandedRowKeys || {}), [String(key)]: true };
    this.persistExpandedRowKeys();
  }

  onRowCollapse(evnt: any): void {
    if (!this.rowExpansion) return;
    const keyField = this.getRowExpansionKeyField();
    const key = evnt?.data?.[keyField];
    if (key === undefined || key === null) return;

    const k = String(key);
    // Always remove the key, even if it's not in expandedRowKeys (PrimeNG might have already removed it)
    const next = { ...(this.expandedRowKeys || {}) };
    delete next[k];
    this.expandedRowKeys = next;
    // Always persist to ensure localStorage is updated
    this.persistExpandedRowKeys();
  }

  getRowExpansionRowClass(nestedRow: any, parentRow: any): string | string[] | Record<string, boolean> | null {
    const fn = this.rowExpansion?.rowClass;
    if (!fn) return null;
    try {
      return (fn(nestedRow, parentRow) as any) || null;
    } catch {
      return null;
    }
  }

  @Input('AllowSelection') set _AllowSelection(value: any) {
    this.allow_selection = value ? value : false;
  }

  @Input('AllowHeaderSelection') set _AllowHeaderSelection(value: any) {
    this.allow_Headerselection = value == false ? value : true;
  }

  @Input('AllowSorting') set _AllowSorting(value: any) {
    this.allow_sorting = value ? value : false;
  }

  @Input('AllowColumnReOrdering') set _AllowColumnReOrdering(value: any) {
    this.reorderableColumns = value ? value : false;
  }

  @Input('AllowPagination') set _AllowPagination(value: any) {
    this.allow_Pagination = value ? value : false;
  }

  @Input('ShowLineLevelAction') set _ShowLineLevelAction(value: any) {
    this.show_livelevelActions = value ? value : false;
  }

  @Input('LineLevelActionsList') set _LineLevelActions(value: any) {
    if (value) {
      this.line_levelActions = [];
      value.forEach((item: any) => {
        this.line_levelActions.push({
          label: item.label,
          disabled: item?.disabled,
          command: (e) => this.onLineAction(e, null),
        });
      });
      //this.line_levelActions = value;
    } else {
      this.line_levelActions = [];
    }
  }

  @Input('ShowEditIcon') set _ShowEditIcon(value: any) {
    this.show_EditIcon = value ? value : false;
  }

  @Input('ShowDeleteIcon') set _ShowDeleteIcon(value: any) {
    this.show_DeleteIcon = value ? value : false;
  }

  @Input('ShowPodIcon') set _ShowPodIcon(value: any) {
    this.show_PodIcon = value ? value : false;
  }

  @Input('ShowAttachmentIcon') set _ShowAttachmentIcon(value: any) {
    this.show_AttachmentIcon = value ? value : false;
  }

  @Input('FreezeActionColumn') set _FreezeActionColumn(value: any) {
    this.freeze_actionColumn = value ? value : false;
  }

  @Input('ShowRowGroup') set _ShowRowGroup(value: any) {
    this.show_RowGroup = value ? value : false;
  }

  @Input('RecordCountPerPage') set _RecordCountPerPage(value: any) {
    this.recordCountPerPage = value ? value : 0;
  }

  @Input('TotalRecords') set _TotalRecords(value: any) {
    this.totalRecords = value ? value : 0;
  }

  @Input('TotalRecordsPerPage') set _TotalRecordsPerPage(value: any) {
    this.totalRecordsPerPage = value ? value : 0;
  }

  @Input('HighlightedSearchItems') set _HighlightedSearchItems(value: any) {
    this.highlightedSearchItems = value ? value : '';
  }

  @Input('AllowHighlightSearching') set _AllowHighlightSearching(value: any) {
    this.allowHighlightSearching = value ? value : false;
  }

  @Input('LastPage') set _LastPage(value: any) {
    this.lastPage = value ? value : '';
  }

  @Input('FirstPage') set _FirstPage(value: any) {
    this.firstPage = value ? value : '';
  }

  @Input('Module') set _Module(value: any) {
    this.module_name = value ? value : null;
  }

  @Input('currentPageReport') set _CurrentPageReport(value: any) {
    this.currentPageReport = value ? value : {};
    //console.log(value);
  }

  @ViewChild('p', { static: false }) paginator: Paginator;
  @ViewChild('ACV_GridCountDialog_Ref')
  ACV_GridCountDialog_Ref: GridCountDialogComponent;

  @Output() RowSelection: EventEmitter<any> = new EventEmitter();
  @Output() PageChange: EventEmitter<any> = new EventEmitter();
  @Output() SortChange: EventEmitter<any> = new EventEmitter();
  @Output() GridAction: EventEmitter<any> = new EventEmitter();
  @Output() cellClicked: EventEmitter<any> = new EventEmitter();
  @Output() editDialogClicked = new EventEmitter<{ data: any; header: any }>();
  @Output() onReload: EventEmitter<any> = new EventEmitter();
  @Output() onPodClickGrid: EventEmitter<any> = new EventEmitter();
  @Output() onViewClickEvent = new EventEmitter();
  @Output() ClickableActionClick = new EventEmitter();

  private flaggedItems: Map<any, { startTime: number, isBlinking: boolean }> = new Map();
  private processedRows: Set<any> = new Set();
  permissions:any
  constructor(
    private highlighter: HighlighterPipe,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private profileService: ProfileService,
  ) {}

  ngOnInit() {
    this.permissions = this.profileService.user_permissions;
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['page_number']) {
        // this.paginator.changePage(params['page_number'] - 1); // Sync paginator
      }
      if (params['page_size']) {
        // console.log("Page Size: ", params['page_size']);  // Debugging message
        const pageSize = parseInt(params['page_size'], 50); // Parsing page size
        this.evenrows = pageSize;

        this.totalRecordsPerPage = pageSize;
      }
      if (params['sort_column']) {
        this.sortField = params['sort_column'];
      }
      if (params['sort_order']) {
        this.sortOrder = params['sort_order'] == 'asc' ? 1 : -1;
      }
      this.cdr.detectChanges(); // Trigger UI updates
    });

   }

  getGridColspan(): number {
    const selectionCol = this.allow_selection ? 1 : 0;
    const expanderCol = this.rowExpansion ? 1 : 0;
    const actionCol = (this.show_livelevelActions || this.show_EditIcon || this.show_DeleteIcon || this.show_AttachmentIcon) ? 1 : 0;
    const columnsCount = this.grid_columns?.length || 0;
    return selectionCol + expanderCol + columnsCount + actionCol;
  }

  getRowExpansionKeyField(): string {
    return this.rowExpansion?.keyField || 'id';
  }

  getRowExpansionColumns(): any[] {
    if (!this.rowExpansion?.columns) return [];
    return this.rowExpansion.columns.filter((c: any) => c?.visible !== false);
  }

  getRowExpansionRows(rowData: any): any[] {
    if (!this.rowExpansion || !rowData) return [];
    const field = this.rowExpansion.detailsField;
    const rows = rowData?.[field];
    return Array.isArray(rows) ? rows : [];
  }

  onSortChange(evnt: any) {
    this.SortChange.emit(evnt);
  }

  onPageSizeChange(evnt: any) {
    this.pagesize_selected = evnt.rows;
    evnt.pageSize = true;
    this.evenrows = evnt.rows;
    this.PageChange.emit(evnt);
  }

  onPageChange(evnt: any) {
    evnt.rows = this.pagesize_selected;
    this.PageChange.emit(evnt);
  }

  onSelectAll(evnt: any) {
    this.AllSelect.emit(evnt);
  }

  onSelectionChange(evnt: any) {
    this.SelectionChange.emit(evnt);
  }

  getLineActionItems(row_data: any) {
   if(this.module_name !== null && this.module_name == 'Leads'){
if (row_data?.status === 'New') {
        return [
          { label: 'Onboard', disabled: !this.permissions?.Customers?.Leads?.edit },
          { label: 'Reject', disabled: !this.permissions?.Customers?.Leads?.reject },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
        ];
      }
      else if(row_data?.status === 'Pending'){
        return [
          { label: 'Onboard', disabled: !this.permissions?.Customers?.Leads?.edit },
          { label: 'Reject', disabled: !this.permissions?.Customers?.Leads?.reject },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
                ];
              }
      else if(row_data?.status === 'Completed'){
return [
          { label: 'Audit', disabled: !this.permissions?.Customers?.Leads?.edit },
          { label: 'Reject', disabled: !this.permissions?.Customers?.Leads?.reject },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
        ];
      }
       else if(row_data?.status === 'In Progress'){
return [
          { label: 'Move to complete', disabled: !this.permissions?.Customers?.Leads?.edit },
          { label: 'Reject', disabled: !this.permissions?.Customers?.Leads?.reject },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
        ];
      }
       else if(row_data?.status === 'Audited'){
return [
          { label: 'Validate', disabled: !this.permissions?.Customers?.Leads?.validate },
          { label: 'Reject', disabled: !this.permissions?.Customers?.Leads?.reject },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
        ];
      }
       else if(row_data?.status === 'Confirmed'){
return [
          { label: 'Move to active customers', disabled: !this.permissions?.Customers?.Leads?.edit },
          { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
        ];
      }
      else if(row_data?.status === 'Validated'){
        return [
                  { label: 'Contract', disabled: !this.permissions?.Customers?.Leads?.edit },
                  { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
                ];
              }
      else {
    return [
      { label: 'Edit', disabled: !this.permissions?.Customers?.Leads?.edit },
    ];
  }

    }
    else if(this.module_name !== null && this.module_name == 'TripRequest'){
      let latestCompletedRow = this.grid_data.filter((leg: { is_latest_completed: number }) => leg.is_latest_completed == 1);
      let isLatestCompletedRow = latestCompletedRow.length > 0 && latestCompletedRow[0].id === row_data.id;

      if (row_data?.status === 'Completed') {
        return [
          // { label: 'Undo', disabled: !isLatestCompletedRow },
          { label: 'Revert Leg', disabled: !isLatestCompletedRow },
        ];
      }

      return this.line_levelActions.map((item) => {

        if(item?.label == "Move to On Hold" && row_data?.status == 'Not Started'){
          return { ...item, disabled: true };
        }
        else if(item?.label == "Move to On Hold" && row_data?.status == 'On Hold'){
          return { ...item, label: 'Resume' };
        }
        else if(item?.label == "Move to Completed" && row_data?.status == 'On Hold'){
          return { ...item, disabled: true };
        }
        else{
          return { ...item };
        }
      });
    }
    else{
      if (row_data?.status == 'Draft') {
        return this.line_levelActions.filter(
          (x: any) =>
            x?.label?.toLowerCase() !== 'active' &&
            x?.label?.toLowerCase() !== 'inactive'
        );
      } else if (row_data?.status === 'Completed') {
        return this.line_levelActions.map((item) => {
          return { ...item, disabled: true };
        });
      } else {
        let status_action_index = this.line_levelActions.findIndex(
          (x: any) =>
            x?.label?.toLowerCase() == 'active' ||
            x?.label?.toLowerCase() == 'inactive'
        );
        if (status_action_index !== -1) {
          this.line_levelActions[status_action_index].label =
            row_data?.status?.toLowerCase() == 'active' ? 'Inactive' : 'Active';
        }

        return this.line_levelActions;
      }
    }
  }

  setHoveredItem(cellId: string) {
    this.currentHoveredCellId = cellId;
  }

  clearHoveredItem() {
    this.currentHoveredCellId = '';
  }

  onGridAction(act: string, actiondata?: any) {
    let actionObj = { action: act, data: actiondata };
    this.GridAction.emit(actionObj);
  }

  onLineAction(evnt: any, row_data: any) {
    if (evnt.label == 'Item notes') {
      this.onGridAction('ItemNotes', {});
    } else {
      if (
        evnt.label.toLowerCase() == 'inactive' ||
        evnt.label.toLowerCase() == 'active'
      ) {
        this.onGridAction(
          this.active_row_data?.status.toLowerCase() == 'inactive'
            ? 'Active'
            : 'InActive',
          this.active_row_data
        );
      } else {
        this.onGridAction(evnt.label, this.active_row_data);
      }
    }
  }

  oncellClicked(evnt: any) {
    evnt && this.cellClicked.emit(evnt);
  }

  onViewClick(col: any, data: any, row: any) {
    // console.log('View Clicked', col, data, row);
    if(col?.manual) {
      this.onViewClickEvent.emit(row);
      return;
    } else if (col?.field == 'capture_pod') {
      this.PodClick.emit(row);
    } else {
    this.ACV_GridCountDialog_Ref.data = data;
    this.ACV_GridCountDialog_Ref.title = col?.dialogTitle
      ? col.dialogTitle
      : col?.header;
      this.ACV_GridCountDialog_Ref.data_type = col?.dataType
      ? col.dataType
      : 'text';
      this.ACV_GridCountDialog_Ref.show();
    }
  }

  editDialogEmit(data: any, header: any) {
    this.editDialogClicked.emit({ data, header });
  }

  onCountClick(col: any, data: any) {
    this.ACV_GridCountDialog_Ref.complete_row_data = data;
    this.ACV_GridCountDialog_Ref.data = data[col?.field];
    this.ACV_GridCountDialog_Ref.gridColumns = col?.count_GridColumns
      ? col.count_GridColumns
      : [];
    this.ACV_GridCountDialog_Ref.data_type = col?.dataType
      ? col.dataType
      : 'list';
    this.ACV_GridCountDialog_Ref.title = col?.dialogTitle
      ? col.dialogTitle
      : col?.header;
    this.ACV_GridCountDialog_Ref.show();
  }

  columnReorderToggle() {
    this.allow_columnReOrder = !this.allow_columnReOrder;
    this.hold_gridSorting = !this.hold_gridSorting;
    this.grid_columns = [...this.grid_columns];
  }

  clearSelection() {
    this.selectedRecords = [];
  }

  resetPagination() {
    this.paginator.changePageToFirst(1);
  }

  setGridSort(column: string, order: string) {
    this.sortField = column;
    this.sortOrder = order == 'desc' ? -1 : 1;
  }

    ngOnDestroy(): void {
      this.flaggedItems.clear();
      this.processedRows.clear();
    }
    shouldShowBlinkingDot(rowData: any): boolean {
      const condition = rowData.last_action_flag === 1;

      if (condition) {
        // If not already processed and not already flagged, start the process
        if (!this.processedRows.has(rowData.id) && !this.flaggedItems.has(rowData.id)) {
          this.processedRows.add(rowData.id);
          this.startBlinkingTimer(rowData);
        }

        // Show dot if currently flagged (within 5-second window)
        return this.flaggedItems.has(rowData.id);
      }
      return false;
    }

    isBlinking(rowData: any): boolean {
      return this.flaggedItems.has(rowData.id);
    }

    private startBlinkingTimer(rowData: any): void {
      const startTime = Date.now();

      // Add to flagged items (this controls the display and blinking)
      this.flaggedItems.set(rowData.id, {
        startTime: startTime,
        isBlinking: true
      });

      // Remove after exactly 5 seconds
      setTimeout(() => {
        this.flaggedItems.delete(rowData.id);
        // Force change detection to update the UI
        if (this.cdr) {
          this.cdr.detectChanges();
        }
      }, 20000); // 20 seconds
    }

    resetProcessedRows(): void {
      this.processedRows.clear();
      this.flaggedItems.clear();
    }
    onClickableActionClick(col: any, rowData: any) {
      this.ClickableActionClick.emit({ column: col, data: rowData });
    }
    getTooltipContent(content: any, col: any, rowData: any): string {
      if (!col.showTooltip) {
        return '';
      }

      // If custom tooltip content function is provided
      if (col.tooltipContent && typeof col.tooltipContent === 'function') {
        return col.tooltipContent(content, rowData);
      }

      // If tooltip field is specified, use that field's value
      if (col.tooltipField && rowData[col.tooltipField]) {
        return rowData[col.tooltipField];
      }

      // Default: return the content itself
      return content || '';
    }

    // Helper method to check if tooltip should be shown
    shouldShowTooltip(content: any, col: any, rowData: any): boolean {
      if (!col.showTooltip) {
        return false;
      }

      // If custom condition function is provided
      if (col.tooltipCondition && typeof col.tooltipCondition === 'function') {
        return col.tooltipCondition(content, rowData);
      }

      // Default: show if content exists and is not empty
      return !!(content && content.toString().trim());
    }




}
